import { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { FaSave } from "react-icons/fa";
import api from '../../api/manage_database/SensorReq';
import { toast } from 'sonner';
import BackButton from "../../components/BackButton";
import SelectSensorType from "../../components/SelectSensorType";
import InputDimension from "../../components/InputDimension";
import InputSerialNumber from "../../components/InputSerialNumber";
import ExistingDeviceChoices from "../../components/ExistingDeviceChoices";

const existingSensorFields = [
    { key: 'type', label: 'Select a sensor type' },
    { key: 'dimension', label: 'Select a dimension' },
    { key: 'serial_number', label: 'Select a serial number' },
];

const SensorNew = ({ embedded = false, onCreated, onSavingChange }) => {

    const [sensorTypes, setSensorTypes] = useState([]);
    const [selectedSensorType, setSelectedSensorType] = useState('');
    const [dimension, setDimension] = useState('');
    const [serialNumber, setSerialNumber] = useState('');
    const [selectedExistingSensor, setSelectedExistingSensor] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const savingRef = useRef(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchSensorTypes = async () => {
            try {
                const response = await api.get('types', {withCredentials: true});
                if (response.data.success) {
                    setSensorTypes(response.data.payload.types);
                } else {
                    toast.error('Not got data from database');
                }
            } catch (err) {
                console.log(err.response);
            }
        };

        fetchSensorTypes();
    }, []);

    const handleInputDimension = (e) => {
        setSelectedExistingSensor('');
        setSaveError('');
        setDimension(e.target.value);    
    };
    
    const handleInputSerialNumber = (e) => {
        setSelectedExistingSensor('');
        setSaveError('');
        setSerialNumber(e.target.value);
    };

    const handleAdd = async () => {
        if (savingRef.current) return;
        setSaveError('');
        if (selectedSensorType === '') {
            toast.error('Set correct sensor Type!')
            return
        }
        
        if (dimension.length > 6) {
            toast.error('Max length of Dimension is 6.')
            return
        }

        if (dimension === '') {
            toast.warning('Dimension field is empty but possible to set!')
        }

        if (serialNumber === '') {
            toast.warning('Serial Number field is empty but possible to set!')
        }

        savingRef.current = true;
        setSaving(true);
        onSavingChange?.(true);
        try {
            const response = await api.post(
                '',
                {
                    dimension: dimension,
                    serial_number: serialNumber,
                    type: selectedSensorType
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                toast.success(response.data.message);
                if (embedded) {
                    onCreated?.(response.data.payload[0]);
                    return;
                }
                navigate(
                    `../record/${response.data.payload[0].id}`,
                    {
                        state: {
                            addRecordMessage: 'Added record to database!',
                            anotherTypeOfPage: true
                        }
                    }
                )
            } else {
                const message = response.data.message || 'Could not add the sensor. Please try again.';
                setSaveError(message);
                toast.error(message);
            }
        } catch (err) {
            const message = err.response?.data?.message || 'Could not add the sensor. Please try again.';
            setSaveError(message);
            toast.error(message);
        } finally {
            savingRef.current = false;
            setSaving(false);
            onSavingChange?.(false);
        }
    };


    return (
        <div className={`sensor-new-container${embedded ? ' sensor-new-inline' : ''}`}>
            {embedded ? <h3 className='sensor-new-title'>Add Sensor</h3> : <h2 className='sensor-new-title'>Sensor</h2>}
            <div className='sensor-new-card'>
                <div className='sensor-new-label-select-wrapper'>
                    <label
                        htmlFor='sensor-type-select'
                        className='bold-label'
                    >
                        Type:
                    </label>
                    <SelectSensorType
                        SensorTypes={sensorTypes}
                        selectedSensorType={selectedSensorType}
                        setSelectedSensorType={(value) => {
                            setSelectedSensorType(value);
                            setSelectedExistingSensor('');
                            setSaveError('');
                        }}
                    />
                </div>
                <div className='sensor-new-label-select-wrapper'>
                    <label
                        htmlFor='sensor-dimension-input'
                        className='bold-label'
                    >
                        Dimension:
                    </label>
                    <InputDimension
                        id="sensor-dimension-input"
                        dimension={dimension}
                        handleInputDimension={handleInputDimension}
                        classNameStyle='sensor-new-input'
                    />
                </div>
                <div className='sensor-new-label-select-wrapper'>
                    <label
                        htmlFor='serial-number-input'
                        className='bold-label'
                    >
                        Serial Number:
                    </label>
                    <InputSerialNumber
                        serialNumber={serialNumber}
                        handleInputSerialNumber={handleInputSerialNumber}
                        classNameStyle='sensor-new-input'
                    />
                </div>
                {saveError && <p className="device-new-inline-error" role="alert">{saveError}</p>}
                {embedded && (
                    <ExistingDeviceChoices
                        api={api}
                        kind="sensor"
                        fields={existingSensorFields}
                        selectedId={selectedExistingSensor}
                        disabled={saving}
                        onSelect={(sensor) => {
                            setSelectedExistingSensor(sensor?.id || '');
                            setSaveError('');
                            if (sensor) {
                                setSelectedSensorType(sensor.type);
                                setDimension(sensor.dimension || '');
                                setSerialNumber(sensor.serial_number || '');
                            }
                        }}
                    />
                )}
                <button type="button" className='sensor-new-button' onClick={handleAdd} disabled={saving}>
                    {saving ? 'Saving...' : embedded ? 'Add sensor' : 'Add'} &nbsp; <FaSave/>
                </button>
            </div>
            {!embedded && <BackButton navigateTo='/manage-database'/>}
        </div>
    );
        
};

export default SensorNew;
